import { FormCommonProps } from "src/components/common/forms/types";
import { DeepPartial } from "react-hook-form";
import { AavoForm, AavoFormContentParams } from "src/components/common/forms/AavoForm";
import { PartRevisionView } from "src/api/generated/erp/db/types/tables/partRevisionView.ts";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { SitePartQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartQueryApi.ts";
import i18n from "i18next";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { FetchAsyncOptionParams } from "src/components/common/inputFields/AsyncSelectField";
import { requireRule } from "src/components/common/forms/validation.ts";
import { concatWithPipe } from "src/utils/strings.tsx";
import { getPartRevisionStateLabel } from "src/api/generated/erp/db/types/enums/partRevisionState.ts";
import { SitePartRevisionQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartRevisionQueryApi";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { CopyPartRevisionBomApi } from "src/api/generated/erp/parts/sitePart/api/bom/copyPartRevisionBomApi.ts";
import { formatIsoString } from "src/utils/dayjsUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CopyPartRevisionBomFormProps extends FormCommonProps<void> {
	targetPartRevision: PartRevisionView;
}

interface FormValues {
	sourcePartId: number;
	sourcePartRevisionId: number;
	copyOperations: boolean;
	copyConfiguratorPriceScript: boolean;
}

export const CopyPartRevisionBomForm = (props: CopyPartRevisionBomFormProps) => {
	const { targetPartRevision, onCompleted, onFormEdited } = props;
	return (
		<AavoForm
			defaultValues={getDefaultValues(targetPartRevision)}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.copyPartRevisionBom.form"),
				data: { targetPartRevisionId: targetPartRevision.partRevisionId },
			}}
			render={(contentParams) => <FormContent {...props} {...contentParams} />}
		/>
	);

	function getDefaultValues(targetPartRevision: PartRevisionView): DeepPartial<FormValues> {
		return {
			sourcePartId: targetPartRevision.partId,
			copyOperations: true,
			copyConfiguratorPriceScript: true,
		};
	}

	async function submit(values: FormValues) {
		await CopyPartRevisionBomApi.copyRevisionBom({
			targetPartRevisionId: targetPartRevision.partRevisionId,
			sourcePartRevisionId: values.sourcePartRevisionId,
			copyOperations: values.copyOperations,
			copyConfiguratorPriceScript: values.copyConfiguratorPriceScript,
		});
	}
};

interface FormContentProps extends CopyPartRevisionBomFormProps, AavoFormContentParams<FormValues> {}

const FormContent = ({ targetPartRevision, watch, control }: FormContentProps) => {
	const { defaultSiteId } = useGlobalInitData();
	return (
		<>
			<FormAsyncSelectField
				control={control}
				name={"sourcePartId"}
				label={i18n.t("from_part")}
				fetchOptions={({ searchQuery, currentSelection }: FetchAsyncOptionParams<number>) =>
					SitePartQueryApi.getPartSelectionOptions({
						currentPartId: currentSelection,
						searchQuery: searchQuery,
						siteId: defaultSiteId,
						withWarehouseLocations: false,
					})
				}
				getOptionKey={(o) => o.partId}
				getOptionLabel={(o) => o.partLongName}
				rules={requireRule()}
			/>
			<FormAsyncSelectField
				control={control}
				name={"sourcePartRevisionId"}
				label={i18n.t("from_revision")}
				fetchOptions={({ searchQuery, currentSelection }: FetchAsyncOptionParams<number>) =>
					SitePartRevisionQueryApi.getPartRevisionSelectionOptions({
						currentPartRevisionId: currentSelection,
						searchQuery: searchQuery,
						partId: watch("sourcePartId"),
						disablePartRevisionId: targetPartRevision.partRevisionId,
					})
				}
				getOptionKey={(o) => o.partRevisionId}
				getOptionLabel={(o) =>
					concatWithPipe(
						o.revisionNumber,
						getPartRevisionStateLabel(o.partRevisionState),
						formatIsoString(o.validFrom),
					)
				}
				rules={requireRule()}
			/>
			<FormCheckbox
				control={control}
				name={"copyOperations"}
				label={i18n.t("copy_operations")}
				defaultChecked={true}
			/>
			<FormCheckbox
				control={control}
				name={"copyConfiguratorPriceScript"}
				label={i18n.t("copy_configurator_price_script")}
				defaultChecked={true}
			/>
		</>
	);
};
